import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  ArrowLeft,
  Target,
  Clock,
  TrendingUp,
  MapPin,
  Layers,
  DollarSign,
  Star,
  CheckCircle,
  Download,
  Copy,
  RefreshCw,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  ChevronLeft,
  AlertTriangle,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";
import WealthsimpleStepsModal from "@/components/WealthsimpleStepsModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// VERIFIED ETF DATABASE (Data as of Dec 31, 2025)
// Sources: BlackRock, Vanguard, BMO, Purpose official websites
// ============================================
const etfDatabase = {
  // ========== CORE ALL-IN-ONE ETFs ==========
  "XEQT": {
    ticker: "XEQT",
    name: "iShares Core Equity ETF Portfolio",
    mer: 0.20,
    whatItIs: "A single ETF that holds thousands of stocks from around the world in one fund.",
    whatItHolds: "Over 9,000 companies globally including Apple, Microsoft, Royal Bank, Shopify, and thousands more across North America, Europe, Asia, and emerging markets.",
    geography: {
      canada: 26.3,
      usa: 41.4,
      international: 27.0,
      emerging: 5.3
    },
    geographyDetailed: "United States 41.4%, Canada 26.3%, Japan 6.1%, United Kingdom 3.6%, France 2.3%, Germany 2.2%, Switzerland 2.3%, Australia 1.9%, Emerging Markets 5.3%",
    sectors: "Technology, Financials, Healthcare, Consumer, Industrials, Energy. All major sectors represented",
    riskLevel: "Medium",
    bestFor: "Long-term investing (10+ years), retirement savings, hands-off investors",
    holdings: 9221,
    lastUpdated: "Jan 27, 2026",
    source: "BlackRock Canada"
  },
  "VEQT": {
    ticker: "VEQT",
    name: "Vanguard All-Equity ETF Portfolio",
    mer: 0.24,
    whatItIs: "Vanguard's all-in-one global stock ETF with more Canadian exposure than XEQT.",
    whatItHolds: "Thousands of stocks worldwide through four underlying Vanguard ETFs covering Canada, US, developed international, and emerging markets.",
    geography: {
      canada: 30.8,
      usa: 45.2,
      international: 17.0,
      emerging: 7.0
    },
    geographyDetailed: "United States 45.2%, Canada 30.8%, Japan 4.0%, United Kingdom 2.4%, China 2.2%, Taiwan 1.5%, France 1.5%, Switzerland 1.5%, Germany 1.4%",
    sectors: "All major sectors with slight tilt toward Canadian financials and resources",
    riskLevel: "Medium",
    bestFor: "Long-term investors who want more Canadian exposure (30%)",
    holdings: 13500,
    lastUpdated: "Dec 31, 2025",
    source: "Vanguard Canada"
  },
  "ZEQT": {
    ticker: "ZEQT",
    name: "BMO All-Equity ETF",
    mer: 0.20,
    whatItIs: "BMO's all-in-one global stock portfolio with balanced geographic exposure.",
    whatItHolds: "Global stocks across developed and emerging markets through BMO's underlying ETFs.",
    geography: {
      canada: 25.0,
      usa: 48.9,
      international: 20.0,
      emerging: 6.1
    },
    geographyDetailed: "United States 48.9%, Canada 25.0%, Japan 3.7%, United Kingdom 2.6%, Switzerland 1.8%, France 1.6%, Germany 1.6%, Australia 1.1%, Emerging Markets 6.1%",
    sectors: "Broad market exposure across all sectors globally",
    riskLevel: "Medium",
    bestFor: "Long-term investors wanting balanced global exposure with lower Canada weight",
    holdings: 9000,
    lastUpdated: "Dec 31, 2025",
    source: "BMO ETFs"
  },
  
  // ========== US EQUITY ETFs ==========
  "VFV": {
    ticker: "VFV",
    name: "Vanguard S&P 500 Index ETF",
    mer: 0.09,
    whatItIs: "Tracks the 500 largest US companies. The most popular stock index in the world.",
    whatItHolds: "500 large US companies including Apple, Microsoft, Amazon, Nvidia, Google, Meta, Tesla, and more. These are America's biggest and most successful businesses.",
    geography: {
      canada: 0,
      usa: 100,
      international: 0,
      emerging: 0
    },
    geographyDetailed: "United States 100%",
    sectors: "Technology ~32%, Financials ~13%, Healthcare ~12%, Consumer Discretionary ~10%, Communication Services ~9%",
    riskLevel: "Medium",
    bestFor: "Long-term growth with US focus (5+ years), investors who want low-cost US exposure",
    holdings: 500,
    lastUpdated: "Dec 31, 2025",
    source: "Vanguard Canada"
  },
  "XUS": {
    ticker: "XUS",
    name: "iShares Core S&P 500 Index ETF",
    mer: 0.09,
    whatItIs: "iShares version of the S&P 500. Another excellent way to own top US companies.",
    whatItHolds: "The same 500 largest US companies as VFV. Choice between VFV and XUS often comes down to which brokerage you use.",
    geography: {
      canada: 0,
      usa: 100,
      international: 0,
      emerging: 0
    },
    geographyDetailed: "United States 100%",
    sectors: "Technology ~32%, Financials ~13%, Healthcare ~12%, Consumer Discretionary ~10%, Communication Services ~9%",
    riskLevel: "Medium",
    bestFor: "Long-term US exposure, investors looking for rock-bottom fees",
    holdings: 500,
    lastUpdated: "Jan 27, 2026",
    source: "BlackRock Canada"
  },
  "QQC": {
    ticker: "QQC",
    name: "Invesco NASDAQ 100 Index ETF",
    mer: 0.21,
    whatItIs: "Tracks the 100 largest non-financial companies on NASDAQ. Heavily weighted toward technology.",
    whatItHolds: "Tech giants like Microsoft, Apple, Amazon, Nvidia, Google, Meta, Tesla, Broadcom, and other innovative companies.",
    geography: {
      canada: 0,
      usa: 100,
      international: 0,
      emerging: 0
    },
    geographyDetailed: "United States 100%",
    sectors: "Technology ~52%, Communication Services ~16%, Consumer Discretionary ~13%, Healthcare ~5%",
    riskLevel: "Medium",
    bestFor: "Long-term growth investors (10+ years) comfortable with tech-focused volatility",
    holdings: 100,
    lastUpdated: "Dec 31, 2025",
    source: "Invesco Canada"
  },
  
  // ========== INTERNATIONAL ETFs ==========
  "XEF": {
    ticker: "XEF",
    name: "iShares Core MSCI EAFE IMI Index ETF",
    mer: 0.22,
    whatItIs: "Developed countries outside North America. Europe, Japan, Australia, and more.",
    whatItHolds: "Large, mid, and small-cap stocks from Japan (Toyota, Sony), UK (BP, HSBC), Germany (SAP), France (LVMH), Switzerland (Nestlé), and Australia.",
    geography: {
      canada: 0,
      usa: 0,
      international: 100,
      emerging: 0
    },
    geographyDetailed: "Japan ~22%, United Kingdom ~15%, France ~11%, Germany ~10%, Switzerland ~10%, Australia ~6%, Netherlands ~5%, Other developed markets ~21%",
    sectors: "Financials, Industrials, Healthcare, Consumer Goods. Diversified across sectors",
    riskLevel: "Medium",
    bestFor: "Adding true international diversification beyond North America",
    holdings: 2800,
    lastUpdated: "Dec 31, 2025",
    source: "BlackRock Canada"
  },
  
  // ========== SAFETY / SHORT-TERM ==========
  "CASH": {
    ticker: "CASH.TO",
    name: "Global X High Interest Savings ETF",
    mer: 0.11,
    whatItIs: "Like a high-interest savings account, but you can hold it in your investment account (TFSA, RRSP).",
    whatItHolds: "Cash deposits at major Canadian banks (National Bank, Scotiabank, CIBC) earning competitive interest rates.",
    geography: {
      canada: 100,
      usa: 0,
      international: 0,
      emerging: 0
    },
    geographyDetailed: "Canada 100% (cash deposits at Canadian banks)",
    sectors: "Cash / Money Market",
    riskLevel: "Very Low",
    bestFor: "Emergency funds, short-term savings (0-2 years), parking cash safely",
    holdings: 1,
    lastUpdated: "Jan 27, 2026",
    source: "Global X Canada"
  },
  "PSA": {
    ticker: "PSA",
    name: "Purpose High Interest Savings ETF",
    mer: 0.12,
    whatItIs: "Another excellent high-interest savings ETF option with slightly different bank mix.",
    whatItHolds: "High-interest deposit accounts and short-term cash equivalents at Canadian financial institutions.",
    geography: {
      canada: 100,
      usa: 0,
      international: 0,
      emerging: 0
    },
    geographyDetailed: "Canada 100% (cash deposits and short-term instruments)",
    sectors: "Cash / Money Market",
    riskLevel: "Very Low",
    bestFor: "Emergency funds, short-term goals, capital preservation",
    holdings: 1,
    lastUpdated: "Dec 31, 2025",
    source: "Purpose Investments"
  },
  "ZAG": {
    ticker: "ZAG",
    name: "BMO Aggregate Bond Index ETF",
    mer: 0.09,
    whatItIs: "Canadian government and corporate bonds. Loans that pay you interest.",
    whatItHolds: "A mix of Canadian federal government bonds (~40%), provincial bonds (~25%), and investment-grade corporate bonds (~35%).",
    geography: {
      canada: 100,
      usa: 0,
      international: 0,
      emerging: 0
    },
    geographyDetailed: "Canada 100% (Canadian bonds only)",
    sectors: "Federal Government Bonds ~40%, Provincial Bonds ~25%, Corporate Bonds ~35%",
    riskLevel: "Low",
    bestFor: "Reducing portfolio volatility, stable income, medium-term goals (2-5 years)",
    holdings: 1400,
    lastUpdated: "Dec 29, 2025",
    source: "BMO ETFs"
  },
};

// ============================================
// QUIZ QUESTIONS
// ============================================
const quizQuestions = [
  {
    id: "goal",
    question: "What's your primary reason for investing?",
    icon: Target,
    options: [
      { value: "retirement", label: "Retirement / long-term wealth", emoji: "🏖️" },
      { value: "house", label: "House down payment (5-10 years)", emoji: "🏠" },
      { value: "emergency", label: "Emergency fund (0-2 years)", emoji: "🛡️" },
      { value: "business", label: "Starting a business (1-5 years)", emoji: "💼" },
      { value: "wealth", label: "General wealth building", emoji: "📈" },
    ],
  },
  {
    id: "timeHorizon",
    question: "When will you need this money?",
    icon: Clock,
    options: [
      { value: "0-2", label: "Less than 2 years", emoji: "⏰" },
      { value: "2-5", label: "2-5 years", emoji: "📅" },
      { value: "5-10", label: "5-10 years", emoji: "🗓️" },
      { value: "10+", label: "10+ years", emoji: "🎯" },
    ],
  },
  {
    id: "riskTolerance",
    question: "If your portfolio dropped 20% in a bad year, what would you do?",
    icon: TrendingUp,
    options: [
      { value: "low", label: "Sell to stop losses", emoji: "😰", desc: "I can't handle big drops" },
      { value: "medium", label: "Feel stressed but hold", emoji: "😬", desc: "I'd be worried but stay the course" },
      { value: "high", label: "Buy more / not worried", emoji: "😎", desc: "Drops are buying opportunities!" },
    ],
  },
  {
    id: "canadaBias",
    question: "How much Canadian exposure do you want?",
    icon: MapPin,
    options: [
      { value: "high", label: "More Canada (30%+)", emoji: "🍁", desc: "I believe in Canadian markets" },
      { value: "balanced", label: "Balanced (20-30%)", emoji: "⚖️", desc: "A healthy mix" },
      { value: "low", label: "Less Canada (under 20%)", emoji: "🌍", desc: "I prefer global diversification" },
      { value: "none", label: "No preference", emoji: "🤷", desc: "You decide for me" },
    ],
  },
  {
    id: "simplicity",
    question: "How many ETFs do you want to manage?",
    icon: Layers,
    options: [
      { value: "1", label: "ONE ETF only", emoji: "1️⃣", desc: "Set it and forget it" },
      { value: "2", label: "Two ETFs", emoji: "2️⃣", desc: "A bit more control" },
      { value: "3", label: "Three ETFs", emoji: "3️⃣", desc: "Maximum customization" },
    ],
  },
  {
    id: "monthlyAmount",
    question: "How much can you invest monthly?",
    icon: DollarSign,
    options: [
      { value: "50", label: "Under $50", emoji: "🌱" },
      { value: "100", label: "$50 - $100", emoji: "🌿" },
      { value: "300", label: "$100 - $300", emoji: "🌳" },
      { value: "500", label: "$300 - $500", emoji: "🌲" },
      { value: "1000", label: "$500+", emoji: "🏔️" },
    ],
  },
  {
    id: "happinessTarget",
    question: "What would make you happy with your investments?",
    icon: Star,
    options: [
      { value: "safety", label: "Just keep my money safe", emoji: "🛡️" },
      { value: "modest", label: "Modest growth, low stress", emoji: "🌱" },
      { value: "balanced", label: "Good growth with some ups and downs", emoji: "⚖️" },
      { value: "growth", label: "Strong growth, I can wait it out", emoji: "📈" },
    ],
  },
];

// ============================================
// RECOMMENDATION ALGORITHM
// Priority: Time horizon > Risk tolerance > Simplicity > Canada bias > Goal
// ============================================
const generateRecommendation = (answers) => {
  const { goal, timeHorizon, riskTolerance, canadaBias, simplicity, monthlyAmount, happinessTarget } = answers;
  
  const maxETFs = parseInt(simplicity) || 1;
  
  let portfolio = [];
  let portfolioName = "";
  let timelineGuidance = "";
  let riskNote = "";
  
  // ============================================
  // CASE 1: SHORT-TERM (< 2 years) or Emergency Fund
  // RULE: Cash-like ETFs only
  // ============================================
  if (timeHorizon === "0-2" || goal === "emergency") {
    portfolioName = "Capital Preservation";
    timelineGuidance = "For money you need within 2 years, safety is the priority. Stock markets can drop 20-30% in any given year, and you may not have time to recover before you need the money.";
    riskNote = "This portfolio prioritizes keeping your money safe over growth.";
    
    portfolio = [
      {
        ...etfDatabase["CASH"],
        allocation: 100,
        reasons: [
          "Your money is needed soon (under 2 years), safety comes first",
          "Earns interest while keeping your money easily accessible",
          "Won't drop in value when stock markets are down"
        ]
      }
    ];
  }
  // ============================================
  // CASE 2: MEDIUM-TERM (2-5 years)
  // RULE: Conservative, bonds with limited equity
  // ============================================
  else if (timeHorizon === "2-5") {
    if (riskTolerance === "low" || happinessTarget === "safety") {
      portfolioName = "Conservative Saver";
      timelineGuidance = "With 2-5 years, you have some time but not enough to fully recover from a major market drop. We're prioritizing stability with modest growth potential.";
      riskNote = "A cautious approach balancing safety with some growth.";
      
      if (maxETFs === 1) {
        portfolio = [
          {
            ...etfDatabase["ZAG"],
            allocation: 100,
            reasons: [
              "Bonds are much more stable than stocks",
              "Your 2-5 year timeline needs protection from big market drops",
              "Provides steady income through interest payments"
            ]
          }
        ];
      } else {
        portfolio = [
          {
            ...etfDatabase["ZAG"],
            allocation: 70,
            reasons: [
              "Bonds provide stability as your main holding",
              "Protects against stock market volatility",
              "Appropriate for your timeline and risk comfort"
            ]
          },
          {
            ...etfDatabase["CASH"],
            allocation: 30,
            reasons: [
              "Cash portion for extra safety and flexibility",
              "Earns interest with virtually no risk",
              "Available immediately if needed sooner"
            ]
          }
        ];
      }
    } else {
      portfolioName = "Balanced Growth";
      timelineGuidance = "With 2-5 years, we're mixing stocks and bonds. The bonds help cushion any stock market drops while still allowing for some growth.";
      riskNote = "A balanced approach with growth potential and downside protection.";
      
      if (maxETFs === 1) {
        portfolio = [
          {
            ...etfDatabase["ZAG"],
            allocation: 100,
            reasons: [
              "For 2-5 year timelines, bonds offer the right balance",
              "More stable than stocks while still earning returns",
              "Protects your savings if markets decline"
            ]
          }
        ];
      } else if (maxETFs === 2) {
        const equityETF = canadaBias === "high" ? "VEQT" : "XEQT";
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 40,
            reasons: [
              "Limited stock exposure for some growth potential",
              "Globally diversified to reduce single-market risk",
              canadaBias === "high" ? "30.8% Canadian exposure matches your preference" : "26.3% Canadian with strong global diversification"
            ]
          },
          {
            ...etfDatabase["ZAG"],
            allocation: 60,
            reasons: [
              "Majority in bonds for stability",
              "Cushions against stock market drops",
              "Appropriate for medium-term goals"
            ]
          }
        ];
      } else {
        const equityETF = canadaBias === "high" ? "VEQT" : "XEQT";
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 35,
            reasons: [
              "Global stocks for growth potential",
              canadaBias === "high" ? "Higher Canadian exposure (30.8%)" : "Balanced global exposure",
              "Limited allocation appropriate for timeline"
            ]
          },
          {
            ...etfDatabase["ZAG"],
            allocation: 45,
            reasons: [
              "Bonds reduce overall portfolio volatility",
              "Provides steady income",
              "Core stability holding"
            ]
          },
          {
            ...etfDatabase["CASH"],
            allocation: 20,
            reasons: [
              "Cash for extra safety buffer",
              "Immediately accessible if needed",
              "Earns interest with no risk"
            ]
          }
        ];
      }
    }
  }
  // ============================================
  // CASE 3: LONG-TERM (5-10 years)
  // RULE: Mostly equity, small bond allocation for cautious investors
  // ============================================
  else if (timeHorizon === "5-10") {
    if (riskTolerance === "low") {
      portfolioName = "Cautious Long-Term Growth";
      timelineGuidance = "5-10 years gives you time to recover from market downturns. Since you prefer less risk, we're including some bonds for stability.";
      riskNote = "Growth-focused with a safety cushion for peace of mind.";
      
      if (maxETFs === 1) {
        const equityETF = selectEquityETF(canadaBias);
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 100,
            reasons: [
              "5-10 years is enough time for stock market recovery",
              "All-in-one fund simplifies your investing",
              getCanadaReason(canadaBias, equityETF)
            ]
          }
        ];
      } else {
        const equityETF = selectEquityETF(canadaBias);
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 75,
            reasons: [
              "Strong equity allocation for long-term growth",
              "Global diversification reduces risk",
              getCanadaReason(canadaBias, equityETF)
            ]
          },
          {
            ...etfDatabase["ZAG"],
            allocation: 25,
            reasons: [
              "Bonds for your comfort with market volatility",
              "Reduces overall portfolio swings",
              "Can gradually reduce as you get comfortable"
            ]
          }
        ];
      }
    } else {
      portfolioName = "Long-Term Growth";
      timelineGuidance = "5-10 years is excellent for equity investing. Markets have historically recovered from every downturn given enough time.";
      riskNote = "A growth-oriented portfolio suitable for your timeline.";
      
      if (maxETFs === 1) {
        const equityETF = selectEquityETF(canadaBias);
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 100,
            reasons: [
              "One-fund solution covering global markets",
              "Automatically diversified across thousands of companies",
              getCanadaReason(canadaBias, equityETF)
            ]
          }
        ];
      } else if (maxETFs === 2) {
        const equityETF = selectEquityETF(canadaBias);
        if (canadaBias === "low") {
          portfolio = [
            {
              ...etfDatabase[equityETF],
              allocation: 70,
              reasons: [
                "Core global equity holding",
                "Broad diversification across all markets",
                "Lower Canadian exposure as you preferred"
              ]
            },
            {
              ...etfDatabase["XEF"],
              allocation: 30,
              reasons: [
                "International developed markets (Europe, Japan, Australia)",
                "True diversification beyond North America",
                "Reduces dependence on US and Canadian markets"
              ]
            }
          ];
        } else {
          portfolio = [
            {
              ...etfDatabase[equityETF],
              allocation: 80,
              reasons: [
                "Your main diversified holding",
                getCanadaReason(canadaBias, equityETF),
                "Covers all major global markets"
              ]
            },
            {
              ...etfDatabase["VFV"],
              allocation: 20,
              reasons: [
                "Extra US exposure. World's largest economy",
                "Very low cost at 0.09% MER",
                "Top 500 US companies"
              ]
            }
          ];
        }
      } else {
        const equityETF = selectEquityETF(canadaBias);
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 60,
            reasons: [
              "Core global holding with instant diversification",
              "Thousands of companies across all sectors",
              getCanadaReason(canadaBias, equityETF)
            ]
          },
          {
            ...etfDatabase["VFV"],
            allocation: 25,
            reasons: [
              "S&P 500 for additional US exposure",
              "Ultra-low 0.09% fee",
              "America's 500 largest companies"
            ]
          },
          {
            ...etfDatabase["XEF"],
            allocation: 15,
            reasons: [
              "International developed markets",
              "Europe, Japan, Australia, and more",
              "Geographic diversification beyond North America"
            ]
          }
        ];
      }
    }
  }
  // ============================================
  // CASE 4: VERY LONG-TERM (10+ years)
  // RULE: Full equity allocation
  // ============================================
  else {
    timelineGuidance = "10+ years is ideal for investing! With this timeline, you can ride out any market ups and downs. Historically, staying invested in stocks has outperformed other investments over long periods.";
    
    if (riskTolerance === "low") {
      portfolioName = "Steady Long-Term Growth";
      riskNote = "Even with a cautious approach, 10+ years allows you to benefit from stock market growth.";
      
      if (maxETFs === 1) {
        const equityETF = selectEquityETF(canadaBias);
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 100,
            reasons: [
              "10+ years = plenty of time for market recovery",
              "Simple one-fund approach",
              getCanadaReason(canadaBias, equityETF)
            ]
          }
        ];
      } else {
        const equityETF = selectEquityETF(canadaBias);
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 85,
            reasons: [
              "Core diversified global holding",
              "Long timeline means stocks are appropriate",
              getCanadaReason(canadaBias, equityETF)
            ]
          },
          {
            ...etfDatabase["ZAG"],
            allocation: 15,
            reasons: [
              "Small bond allocation for your comfort",
              "Slightly reduces volatility",
              "Can remove as you become more comfortable"
            ]
          }
        ];
      }
    } else {
      portfolioName = "All-Equity Growth";
      riskNote = "A fully invested portfolio designed for maximum long-term growth.";
      
      if (maxETFs === 1) {
        const equityETF = selectEquityETF(canadaBias);
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 100,
            reasons: [
              "100% stocks for long-term growth",
              "Perfect for 10+ year timeline",
              getCanadaReason(canadaBias, equityETF)
            ]
          }
        ];
      } else if (maxETFs === 2) {
        const equityETF = selectEquityETF(canadaBias);
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 75,
            reasons: [
              "Core global equity holding",
              "Diversified across all markets",
              getCanadaReason(canadaBias, equityETF)
            ]
          },
          {
            ...etfDatabase["VFV"],
            allocation: 25,
            reasons: [
              "Additional US market exposure",
              "S&P 500 has strong historical returns",
              "Ultra-low 0.09% fee"
            ]
          }
        ];
      } else {
        const equityETF = selectEquityETF(canadaBias);
        portfolio = [
          {
            ...etfDatabase[equityETF],
            allocation: 55,
            reasons: [
              "Diversified global foundation",
              "Covers developed and emerging markets",
              getCanadaReason(canadaBias, equityETF)
            ]
          },
          {
            ...etfDatabase["VFV"],
            allocation: 30,
            reasons: [
              "S&P 500 for US large-cap growth",
              "Top American companies",
              "Very low cost"
            ]
          },
          {
            ...etfDatabase["XEF"],
            allocation: 15,
            reasons: [
              "International developed markets",
              "Europe, Japan, Australia exposure",
              "Reduces North America concentration"
            ]
          }
        ];
      }
    }
  }
  
  // Calculate weighted MER
  const weightedMER = portfolio.reduce((sum, etf) => {
    return sum + (etf.mer * etf.allocation / 100);
  }, 0);
  
  return {
    portfolioName,
    portfolio,
    weightedMER: weightedMER.toFixed(2),
    timelineGuidance,
    riskNote,
    answers,
    timeHorizon,
    riskTolerance,
  };
};

// Helper: Select equity ETF based on Canada preference
function selectEquityETF(canadaBias) {
  if (canadaBias === "high") return "VEQT"; // 30.8% Canada
  if (canadaBias === "balanced") return "XEQT"; // 26.3% Canada
  if (canadaBias === "low") return "ZEQT"; // 25.0% Canada
  return "XEQT"; // Default
}

// Helper: Get Canada-specific reason
function getCanadaReason(canadaBias, etf) {
  const data = etfDatabase[etf];
  if (!data) return "Balanced geographic exposure";
  
  const canadaPercent = data.geography.canada;
  if (canadaBias === "high") {
    return `${canadaPercent}% Canadian exposure matches your preference for more Canada`;
  } else if (canadaBias === "low") {
    return `${canadaPercent}% Canadian exposure - more globally diversified as you wanted`;
  } else {
    return `${canadaPercent}% Canadian exposure provides balanced home-country allocation`;
  }
}

// ============================================
// COMPONENT
// ============================================
const ETFQuizContent = ({ onBack }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState({ rating: null });
  const [quizId, setQuizId] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [wealthsimpleModalOpen, setWealthsimpleModalOpen] = useState(false);
  
  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQ = quizQuestions[currentQuestion];
  
  const submitQuizResults = async (result) => {
    try {
      const userId = localStorage.getItem("user_data") 
        ? JSON.parse(localStorage.getItem("user_data")).id 
        : null;
      
      const response = await axios.post(`${API_URL}/api/etf-quiz/submit`, {
        user_id: userId,
        answers: result.answers,
        portfolio_name: result.portfolioName,
        etf_allocations: result.portfolio.map(p => ({ ticker: p.ticker, allocation: p.allocation })),
        weighted_mer: parseFloat(result.weightedMER),
        risk_bucket: result.portfolioName,
        projections: {}
      });
      setQuizId(response.data.id);
    } catch (error) {
      console.error("Failed to submit quiz results:", error);
    }
  };
  
  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentQ.id]: value };
    setAnswers(newAnswers);
    
    if (currentQuestion < quizQuestions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    } else {
      const result = generateRecommendation(newAnswers);
      setRecommendation(result);
      setShowResults(true);
      submitQuizResults(result);
    }
  };
  
  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  
  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setRecommendation(null);
    setShowFeedback(false);
    setFeedback({ rating: null });
    setQuizId(null);
    setHasStarted(false);
  };
  
  const handleStartQuiz = () => {
    setHasStarted(true);
  };
  
  const handleDownloadPDF = async () => {
    const element = document.getElementById("portfolio-results");
    if (!element) return;
    
    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save("my-etf-portfolio.pdf");
      toast.success("Portfolio downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };
  
  const handleCopyText = () => {
    if (!recommendation) return;
    
    const text = `My ETF Portfolio: ${recommendation.portfolioName}\n\n` +
      recommendation.portfolio.map(p => `${p.ticker}: ${p.allocation}%`).join("\n") +
      `\nWeighted MER: ${recommendation.weightedMER}%` +
      `\n\n${recommendation.timelineGuidance}` +
      `\n\nGenerated by Compoundr - compoundr.ca`;
    
    navigator.clipboard.writeText(text);
    toast.success("Portfolio copied to clipboard!");
  };
  
  const handleFeedback = async (rating) => {
    setFeedback({ rating });
    setShowFeedback(true);
    
    if (quizId) {
      try {
        await axios.post(`${API_URL}/api/etf-quiz/feedback`, {
          quiz_id: quizId,
          rating: rating,
          suggestion: null
        });
      } catch (error) {
        console.error("Failed to submit feedback:", error);
      }
    }
  };
  
  // ============================================
  // INTRO SCREEN
  // ============================================
  if (!hasStarted) {
    return (
      <div className="py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => window.location.href = '/tools'}
            className="mb-6 text-slate-600 hover:text-navy-700"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to All Tools
          </Button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <TrendingUp className="w-10 h-10 text-emerald-600" />
                </div>
                <h1 className="text-3xl font-heading font-bold text-navy-700 mb-4">
                  ETF Portfolio Builder
                </h1>
                <p className="text-slate-600 mb-6">
                  Answer 7 simple questions and get a personalized ETF portfolio recommendation. 
                  No jargon, no complicated math. Just a clear plan to start investing.
                </p>
                
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                  <p className="text-sm text-slate-600 mb-2 font-medium">What you'll get:</p>
                  <ul className="text-sm text-slate-500 space-y-1">
                    <li>✓ 1-3 ETF recommendations tailored to you</li>
                    <li>✓ Clear allocation percentages</li>
                    <li>✓ Investing timeline guidance</li>
                    <li>✓ Downloadable one-page portfolio plan</li>
                  </ul>
                </div>
                
                <Button
                  onClick={handleStartQuiz}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-14 text-lg mb-4"
                  data-testid="start-etf-quiz-btn"
                >
                  Start Quiz (2 min)
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                <div className="border-t pt-4">
                  <p className="text-xs text-slate-400 mb-2">
                    Haven't opened an investment account yet?
                  </p>
                  <button
                    onClick={() => setWealthsimpleModalOpen(true)}
                    className="text-emerald-600 text-sm font-medium hover:underline cursor-pointer"
                  >
                    Get free $25 towards your Wealthsimple TFSA account →
                  </button>
                </div>
              </CardContent>
            </Card>
            
            {/* Wealthsimple Steps Modal */}
            <WealthsimpleStepsModal 
              open={wealthsimpleModalOpen} 
              onOpenChange={setWealthsimpleModalOpen} 
            />
            
            <p className="text-center text-xs text-slate-400 mt-4">
              Not investment advice. Educational purposes only.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RESULTS SCREEN
  // ============================================
  if (showResults && recommendation) {
    return (
      <div className="py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div id="portfolio-results">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Header */}
              <div className="text-center mb-8">
                <Badge className="bg-emerald-100 text-emerald-700 mb-4">
                  Your Personalized Portfolio
                </Badge>
                <h1 className="text-3xl font-heading font-bold text-navy-700 mb-2">
                  {recommendation.portfolioName}
                </h1>
                <p className="text-slate-600">
                  Based on your answers, here's your recommended ETF portfolio
                </p>
              </div>
              
              {/* Timeline Guidance Card */}
              <Card className="border-0 shadow-lg mb-6 bg-blue-50 border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-navy-700 mb-1">Timeline Guidance</h3>
                      <p className="text-slate-600 text-sm">{recommendation.timelineGuidance}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Portfolio Summary Card */}
              <Card className="border-0 shadow-xl mb-6">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-sm text-slate-500">Total ETFs</p>
                      <p className="text-2xl font-bold text-navy-700">{recommendation.portfolio.length}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-slate-500">Total Cost (MER)</p>
                      <p className="text-2xl font-bold text-emerald-600">{recommendation.weightedMER}%</p>
                    </div>
                  </div>
                  
                  {/* MER Explanation */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-slate-700">What is MER?</p>
                        <p className="text-xs text-slate-500">
                          MER (Management Expense Ratio) is the annual fee you pay to own an ETF. 
                          For example, {recommendation.weightedMER}% MER means you pay about ${(parseFloat(recommendation.weightedMER) * 10).toFixed(0)} per year for every $1,000 invested. 
                          This fee is automatically deducted from the fund. You won't see a separate charge.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Allocation Bar */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Your Allocation</p>
                    <div className="h-10 rounded-full overflow-hidden flex">
                      {recommendation.portfolio.map((p, i) => (
                        <div
                          key={p.ticker}
                          className={`flex items-center justify-center text-white text-sm font-medium ${
                            i === 0 ? "bg-emerald-500" : i === 1 ? "bg-blue-500" : "bg-purple-500"
                          }`}
                          style={{ width: `${p.allocation}%` }}
                        >
                          {p.ticker} {p.allocation}%
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {recommendation.riskNote && (
                    <p className="text-sm text-slate-500 italic">{recommendation.riskNote}</p>
                  )}
                </CardContent>
              </Card>
              
              {/* ETF Details */}
              <div className="space-y-4 mb-6">
                {recommendation.portfolio.map((etf, index) => (
                  <Card key={etf.ticker} className="border-0 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-3 h-3 rounded-full ${
                              index === 0 ? "bg-emerald-500" : index === 1 ? "bg-blue-500" : "bg-purple-500"
                            }`} />
                            <h3 className="font-heading font-bold text-xl text-navy-700">
                              {etf.ticker}
                            </h3>
                            <Badge variant="secondary" className="text-sm">{etf.allocation}%</Badge>
                          </div>
                          <p className="text-slate-500">{etf.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">MER</p>
                          <p className="font-bold text-emerald-600">{etf.mer}%</p>
                        </div>
                      </div>
                      
                      {/* What it is */}
                      <div className="mb-4">
                        <p className="text-sm font-medium text-slate-700 mb-1">What it is</p>
                        <p className="text-slate-600 text-sm">{etf.whatItIs}</p>
                      </div>
                      
                      {/* What it holds */}
                      <div className="mb-4">
                        <p className="text-sm font-medium text-slate-700 mb-1">What it holds</p>
                        <p className="text-slate-600 text-sm">{etf.whatItHolds}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 mb-1">Geographic Breakdown</p>
                          <p className="text-sm text-slate-700">{etf.geographyDetailed}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 mb-1">Risk Level</p>
                          <p className={`text-sm font-medium ${
                            etf.riskLevel === "Very Low" ? "text-emerald-600" :
                            etf.riskLevel === "Low" ? "text-emerald-600" :
                            etf.riskLevel === "Low-Medium" ? "text-blue-600" :
                            "text-blue-600"
                          }`}>
                            {etf.riskLevel}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">Best for: {etf.bestFor}</p>
                        </div>
                      </div>
                      
                      {/* Data source */}
                      <div className="text-xs text-slate-400 mb-3">
                        Data from {etf.source} as of {etf.lastUpdated}
                      </div>
                      
                      {/* Why this ETF fits YOU */}
                      <div className="bg-emerald-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-emerald-800 mb-2">Why this ETF fits YOU:</p>
                        <div className="space-y-2">
                          {etf.reasons.map((reason, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-emerald-700">
                              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                              <span>{reason}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Next Steps */}
              <Card className="border-0 shadow-xl mb-6">
                <CardContent className="p-6">
                  <h3 className="font-heading font-bold text-xl text-navy-700 mb-4">
                    How to Buy These ETFs
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-emerald-600 text-sm font-bold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-navy-700">Open a TFSA or RRSP account</p>
                        <button
                          onClick={() => setWealthsimpleModalOpen(true)}
                          className="text-emerald-600 text-sm hover:underline flex items-center gap-1 cursor-pointer"
                        >
                          Get free $25 towards your Wealthsimple TFSA account
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-emerald-600 text-sm font-bold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-navy-700">Search for each ETF ticker</p>
                        <p className="text-sm text-slate-500">
                          {recommendation.portfolio.map(p => p.ticker).join(", ")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-emerald-600 text-sm font-bold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-navy-700">Buy according to your allocation</p>
                        <p className="text-sm text-slate-500">
                          {recommendation.portfolio.map(p => `${p.ticker}: ${p.allocation}%`).join(" • ")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-emerald-600 text-sm font-bold">4</span>
                      </div>
                      <p className="font-medium text-navy-700">Set up automatic deposits and let it grow!</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Disclaimer */}
              <Card className="border-0 shadow-lg mb-6 bg-amber-50 border-l-4 border-l-amber-400">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800 mb-1">Important Disclaimer</p>
                      <p className="text-sm text-amber-700">
                        This is not investment advice. It's an educational tool to help you learn and get started. 
                        Markets go up and down, and investing always involves risk. Do your own research before investing.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Button onClick={handleDownloadPDF} className="flex-1 bg-emerald-500 hover:bg-emerald-600">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button onClick={handleCopyText} variant="outline" className="flex-1">
              <Copy className="w-4 h-4 mr-2" />
              Copy as Text
            </Button>
            <Button onClick={handleRestart} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
          </div>
          
          {/* Feedback Section */}
          {!showFeedback ? (
            <Card className="border-0 shadow-lg mb-6">
              <CardContent className="p-6 text-center">
                <p className="font-medium text-navy-700 mb-4">Was this recommendation helpful?</p>
                <div className="flex justify-center gap-4">
                  <Button
                    onClick={() => handleFeedback("yes")}
                    variant="outline"
                    className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Yes, makes sense
                  </Button>
                  <Button
                    onClick={() => handleFeedback("somewhat")}
                    variant="outline"
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Somewhat
                  </Button>
                  <Button
                    onClick={() => handleFeedback("no")}
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    Not what I expected
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-lg mb-6 bg-emerald-50">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <p className="font-medium text-navy-700">Thanks for your feedback!</p>
                <p className="text-sm text-slate-500">It helps us improve recommendations for everyone.</p>
              </CardContent>
            </Card>
          )}
          
          {/* Back to All Tools */}
          <div className="text-center mb-6">
            <Button variant="ghost" onClick={() => window.location.href = '/tools'} className="text-slate-600">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to All Tools
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // ============================================
  // QUIZ QUESTIONS SCREEN
  // ============================================
  return (
    <div className="py-12">
      <div className="max-w-2xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => window.location.href = '/tools'}
          className="mb-4 text-slate-600 hover:text-navy-700"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to All Tools
        </Button>
        
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">
              Question {currentQuestion + 1} of {quizQuestions.length}
            </span>
            <span className="text-sm text-slate-500">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
        
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <currentQ.icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h2 className="text-xl font-heading font-bold text-navy-700">
                    {currentQ.question}
                  </h2>
                </div>
                
                <div className="space-y-3">
                  {currentQ.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(option.value)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:border-emerald-500 hover:bg-emerald-50 ${
                        answers[currentQ.id] === option.value
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-slate-200"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{option.emoji}</span>
                        <div>
                          <p className="font-medium text-navy-700">{option.label}</p>
                          {option.desc && (
                            <p className="text-sm text-slate-500">{option.desc}</p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                
                {currentQuestion > 0 && (
                  <Button
                    onClick={handleBack}
                    variant="ghost"
                    className="mt-6 text-slate-500"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ETFQuizContent;
